/**
 * Dedicated Followers / Following list page.
 * One component reused for both — driven by the `mode` prop ("followers" | "following").
 * Routes:
 *   /social/followers
 *   /social/following
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Loader2, Users, UserCheck, ArrowLeft, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { getImageUrl } from '../utils/imageUtils';

export const FollowListPage = ({ mode }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const isFollowers = mode === 'followers';
  const title = isFollowers ? 'Followers' : 'Following';
  const Icon = isFollowers ? Users : UserCheck;
  const endpoint = isFollowers ? '/social/followers' : '/social/following';
  const dataKey = isFollowers ? 'followers' : 'following';

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api().get(endpoint);
        if (active) setList(res.data[dataKey] || []);
      } catch {
        if (active) setList([]);
      }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [api, endpoint, dataKey]);

  const filtered = query.trim()
    ? list.filter((u) => (u.name || '').toLowerCase().includes(query.toLowerCase()))
    : list;

  return (
    <div className="min-h-screen bg-black" data-testid={`${mode}-page`}>
      <NavBar />
      <main className="container mx-auto px-6 py-10 max-w-3xl">
        <div className="flex items-center gap-3 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-primary"
            data-testid="follow-list-back"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center">
              <Icon className="h-5 w-5 text-primary" />
            </span>
            <div>
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider">
                {title} <span className="text-primary">({list.length})</span>
              </h1>
              <p className="text-muted-foreground text-sm">
                {isFollowers ? 'People who follow you' : 'People you follow'}
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        {list.length > 4 && (
          <div className="relative mb-6">
            <Search className="h-4 w-4 text-muted-foreground absolute top-1/2 -translate-y-1/2 left-3" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${title.toLowerCase()}...`}
              className="w-full bg-card border border-border rounded-2xl pl-10 pr-4 py-3 text-white placeholder:text-muted-foreground focus:border-primary outline-none transition-colors"
              data-testid="follow-list-search"
            />
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-2xl p-12 text-center"
            data-testid="follow-list-empty"
          >
            <Icon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">
              {query ? 'No matches' : isFollowers ? 'No followers yet' : 'Not following anyone yet'}
            </h3>
            <p className="text-muted-foreground mb-6 text-sm">
              {query
                ? 'Try a different name'
                : isFollowers
                  ? 'Share posts and engage with the community to grow your followers'
                  : 'Discover other members and follow them to see their fitness journey'}
            </p>
            {!query && (
              <Link to="/discover">
                <Button className="bg-primary text-black font-heading tracking-wider">
                  Discover People
                </Button>
              </Link>
            )}
          </motion.div>
        ) : (
          <div className="space-y-3">
            {filtered.map((u, i) => (
              <motion.div
                key={u.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.3) }}
              >
                <Link
                  to={`/user/${u.id}`}
                  className="flex items-center gap-4 bg-card border border-border rounded-2xl p-4 hover:border-primary/50 hover:bg-white/5 transition-colors card-hover"
                  data-testid={`${mode}-row-${u.id}`}
                >
                  <Avatar className="h-12 w-12 border-2 border-primary/20">
                    <AvatarImage src={getImageUrl(u.profile_picture)} />
                    <AvatarFallback className="bg-primary/20 text-primary">
                      {u.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium truncate">{u.name}</p>
                    {u.username && (
                      <p className="text-muted-foreground text-xs truncate">@{u.username}</p>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default FollowListPage;
